// =============================================================================
// Dashboard Page Component
// =============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Play, Settings, Users, Activity, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { workspaceService } from '../services/workspaceService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);

      // Load workspaces and stats in parallel
      const [workspacesData, statsData] = await Promise.all([
        workspaceService.getWorkspaces(),
        workspaceService.getWorkspaceStats()
      ]);

      setWorkspaces(workspacesData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartWorkspace = async (workspaceId) => {
    try {
      await workspaceService.startWorkspace(workspaceId);
      toast.success('Workspace started successfully');
      loadDashboardData(); // Refresh data
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleStopWorkspace = async (workspaceId) => {
    try {
      await workspaceService.stopWorkspace(workspaceId);
      toast.success('Workspace stopped successfully');
      loadDashboardData(); // Refresh data
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-muted-foreground">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Welcome back, {user?.fullName || user?.username}!
          </h1>
          <p className="text-muted-foreground mt-1">
            Ready to build something amazing with AI assistance?
          </p>
        </div>
        
        <button className="btn btn-default flex items-center gap-2">
          <Plus className="w-4 h-4" />
          New Workspace
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-content p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Total Workspaces
                </p>
                <p className="text-2xl font-bold text-foreground">{stats?.total || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-content p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Running
                </p>
                <p className="text-2xl font-bold text-foreground">{stats?.running || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-content p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Stopped
                </p>
                <p className="text-2xl font-bold text-foreground">{stats?.stopped || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-content p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Templates Used
                </p>
                <p className="text-2xl font-bold text-foreground">
                  {stats?.byTemplate ? Object.keys(stats.byTemplate).length : 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
                <Settings className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Workspaces */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="card-title">Recent Workspaces</h3>
              <p className="card-description">
                Your recently accessed development environments
              </p>
            </div>
            <button className="btn btn-outline btn-sm">
              View All
            </button>
          </div>
        </div>
        <div className="card-content">
          {workspaces.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center mb-4">
                <Activity className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No workspaces yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first workspace to get started with AI-powered development
              </p>
              <button
                className="btn btn-default"
                onClick={() => navigate('/workspaces/create')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Workspace
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {workspaces.slice(0, 3).map((workspace) => {
                const templateColors = {
                  nodejs: { bg: 'bg-green-100 dark:bg-green-900/20', text: 'text-green-600 dark:text-green-400', icon: 'JS' },
                  python: { bg: 'bg-blue-100 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', icon: 'PY' },
                  go: { bg: 'bg-cyan-100 dark:bg-cyan-900/20', text: 'text-cyan-600 dark:text-cyan-400', icon: 'GO' },
                  rust: { bg: 'bg-orange-100 dark:bg-orange-900/20', text: 'text-orange-600 dark:text-orange-400', icon: 'RS' },
                  default: { bg: 'bg-gray-100 dark:bg-gray-900/20', text: 'text-gray-600 dark:text-gray-400', icon: '??' }
                };

                const template = templateColors[workspace.templateId] || templateColors.default;
                const isRunning = workspace.status === 'running';

                return (
                  <div
                    key={workspace.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/workspaces/${workspace.id}`)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 ${template.bg} rounded-lg flex items-center justify-center`}>
                        <span className={`text-sm font-medium ${template.text}`}>
                          {template.icon}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">{workspace.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {workspace.description || `${workspace.templateId} workspace`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`badge ${isRunning ? 'badge-default' : 'badge-secondary'}`}>
                        {workspace.status}
                      </span>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          isRunning ? handleStopWorkspace(workspace.id) : handleStartWorkspace(workspace.id);
                        }}
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          className="card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/workspaces/create')}
        >
          <div className="card-content p-6 text-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Plus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-semibold text-foreground mb-2">Create Workspace</h3>
            <p className="text-sm text-muted-foreground">
              Start a new development environment with AI assistance
            </p>
          </div>
        </div>

        <div
          className="card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/terminal-test')}
        >
          <div className="card-content p-6 text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Activity className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="font-semibold text-foreground mb-2">Terminal Test</h3>
            <p className="text-sm text-muted-foreground">
              Test terminal functionality with running workspaces
            </p>
          </div>
        </div>

        <div
          className="card cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/chat-test')}
        >
          <div className="card-content p-6 text-center">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Zap className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="font-semibold text-foreground mb-2">Chat Test</h3>
            <p className="text-sm text-muted-foreground">
              Test Claude AI chat with workspace context
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
